import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../api';
import Select from '../../components/Select';
import Field from '../../components/ui/Field';
import Estado from '../../components/ui/Estado';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/ui/PageHeader';
import BarChart from '../../components/ui/BarChart';
import DataTable from '../../components/ui/DataTable';
import Metrica, { MetricaGrid } from '../../components/ui/Metrica';
import { fmt, fmtBRL } from '../../utils/format';
import { formatarProducaoCurta, labelUnidadePlural, unidadeArea } from '../../utils/unidades';
import { CORES_GRAFICO, classeSinal, tomSinal } from '../../utils/graficos';

export default function CompararAreas() {
  const [safras, setSafras] = useState([]);
  const [safraId, setSafraId] = useState('');
  const [resultado, setResultado] = useState(null);
  const [selecionadas, setSelecionadas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [erroSafras, setErroSafras] = useState('');

  useEffect(() => {
    api.getSafras()
      .then((lista) => {
        const ord = [...(lista || [])].reverse();
        setSafras(ord);
        if (ord.length) {
          setSafraId((atual) => atual || String(ord[0].id));
        }
      })
      .catch((e) => setErroSafras(e.message || 'Erro ao carregar safras.'));
  }, []);

  const carregar = useCallback(async () => {
    if (!safraId) return;
    setCarregando(true);
    setErro('');
    try {
      const r = await api.getResultadoSafra(safraId);
      setResultado(r);
      const ids = (r.lavouras || [])
        .filter((l) => !l.removidaDaSafra)
        .map((l) => l.safraLavouraId);
      setSelecionadas(ids);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar o resultado.');
      setResultado(null);
    } finally {
      setCarregando(false);
    }
  }, [safraId]);

  useEffect(() => { carregar(); }, [carregar]);

  const areasDisponiveis = useMemo(() => {
    if (!resultado?.lavouras) return [];
    return resultado.lavouras.filter((l) => !l.removidaDaSafra);
  }, [resultado]);

  const areas = useMemo(() => {
    return areasDisponiveis.filter((l) => selecionadas.includes(l.safraLavouraId));
  }, [areasDisponiveis, selecionadas]);

  const toggleArea = (id) => {
    setSelecionadas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const prodPorHa = areas.map((l, i) => {
    const area = l.areaHectares || 0;
    const prod = l.totalProduzidoUnidade || 0;
    const porHa = area > 0 && l.tipoArea !== 'ESTUFA' ? prod / area : prod;
    return {
      label: l.lavouraDescricao,
      value: porHa || 0,
      color: CORES_GRAFICO[i % CORES_GRAFICO.length],
      unidade: l.unidadeProducao,
    };
  });

  const custoPorHa = areas.map((l, i) => ({
    label: l.lavouraDescricao,
    value: l.tipoArea === 'ESTUFA'
      ? (l.custoTotalBRL || 0)
      : (l.custoPorHectareBRL ?? 0),
    color: CORES_GRAFICO[i % CORES_GRAFICO.length],
  }));

  const lucroPorHa = areas.map((l, i) => ({
    label: l.lavouraDescricao,
    value: l.tipoArea === 'ESTUFA'
      ? (l.resultadoBRL || 0)
      : (l.resultadoPorHectareBRL ?? 0),
      color: CORES_GRAFICO[i % CORES_GRAFICO.length],
  }));

  const lucroTotal = areas.reduce((s, l) => s + (l.resultadoBRL || 0), 0);
  const melhor = areas.reduce((acc, l) => {
    if (l.resultadoBRL == null) return acc;
    if (!acc || l.resultadoBRL > acc.resultadoBRL) return l;
    return acc;
  }, null);

  const msgInsuficiente = (() => {
    if (!resultado || carregando || erro) return null;
    if (areasDisponiveis.length < 2) {
      return {
        titulo: 'Não há duas áreas para comparar',
        descricao:
          'Cadastre pelo menos duas áreas de cultivo (plantios) nesta safra para usar a comparação.',
      };
    }
    if (areas.length < 2) {
      return {
        titulo: 'Selecione ao menos duas áreas',
        descricao: 'Marque duas ou mais áreas acima para montar os gráficos.',
      };
    }
    return null;
  })();

  return (
    <div>
      <PageHeader
        titulo="Comparar áreas"
        icone="comparar-areas"
        descricao="Talhão × talhão na mesma safra — produtividade, custo e lucro."
        voltar={{ to: '/relatorios', label: 'Relatórios' }}
      >
        <div className="filtros">
          <Field label="Safra" obrigatorio>
            <Select
              value={safraId}
              onChange={setSafraId}
              options={safras.map((s) => ({
                value: String(s.id),
                label: s.descricao + (s.cultura ? ` (${s.cultura})` : ''),
              }))}
              placeholder={erroSafras || 'Selecione a safra'}
              disabled={!safras.length}
            />
          </Field>
        </div>

        {areasDisponiveis.length >= 2 && (
          <div className="chips">
            <span className="chips-label">Áreas na comparação (mín. 2)</span>
            {areasDisponiveis.map((l) => {
              const on = selecionadas.includes(l.safraLavouraId);
              return (
                <button
                  key={l.safraLavouraId}
                  type="button"
                  className={`chip ${on ? 'chip--ativo' : ''}`}
                  onClick={() => toggleArea(l.safraLavouraId)}
                >
                  {l.lavouraDescricao}
                </button>
              );
            })}
          </div>
        )}
      </PageHeader>

      <Estado carregando={carregando} erro={erro} aoTentarNovamente={carregar} itens={4}>
        {msgInsuficiente ? (
          <EmptyState
            compacto
            icone="comparar-areas"
            titulo={msgInsuficiente.titulo}
            descricao={msgInsuficiente.descricao}
            acao={areasDisponiveis.length < 2
              ? { to: '/safras', label: 'Ir para Safras' }
              : undefined}
          />
        ) : (
          <>
            <MetricaGrid min={140}>
              <Metrica label="Áreas" value={String(areas.length)} />
              <Metrica
                label="Melhor lucro"
                value={melhor ? fmtBRL(melhor.resultadoBRL) : '—'}
                detalhe={melhor?.lavouraDescricao}
                tom={melhor ? tomSinal(melhor.resultadoBRL) : undefined}
                destaque
              />
              <Metrica
                label="Lucro total"
                value={fmtBRL(lucroTotal)}
                tom={tomSinal(lucroTotal)}
                destaque
              />
            </MetricaGrid>

            <div className="grade-graficos">
              <div className="card">
                <h2>Produção por área</h2>
                <p className="secao-sub">
                  Por hectare em lavouras; em estufa mostra a produção total da unidade.
                </p>
                <BarChart
                  data={prodPorHa}
                  formatValue={(v) => fmt(v)}
                  height={28}
                  labelWidth={110}
                />
                {prodPorHa[0]?.unidade && (
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                    Unidade de referência: {labelUnidadePlural(prodPorHa[0].unidade)}
                    {areas.some((a) => a.tipoArea !== 'ESTUFA') ? ' / ha' : ''}
                  </p>
                )}
              </div>

              <div className="card">
                <h2>Custo por área</h2>
                <BarChart
                  data={custoPorHa}
                  formatValue={fmtBRL}
                  height={28}
                  labelWidth={110}
                />
              </div>

              <div className="card">
                <h2>Lucro por área</h2>
                <BarChart
                  data={lucroPorHa}
                  formatValue={fmtBRL}
                  height={28}
                  labelWidth={110}
                />
              </div>
            </div>

            <div className="card">
              <h2>Resumo</h2>
              <DataTable
                linhas={areas}
                chaveDe={(l) => l.safraLavouraId}
                colunas={[
                  {
                    chave: 'nome',
                    titulo: 'Área',
                    papel: 'titulo',
                    render: (l) => l.lavouraDescricao,
                  },
                  {
                    chave: 'tam',
                    titulo: 'Tamanho',
                    render: (l) =>
                      l.areaHectares != null
                        ? `${fmt(l.areaHectares)} ${unidadeArea(l.tipoArea)}`
                        : '—',
                  },
                  {
                    chave: 'prod',
                    titulo: 'Produção',
                    render: (l) =>
                      formatarProducaoCurta(l.totalProduzidoUnidade, l.unidadeProducao),
                  },
                  {
                    chave: 'custo',
                    titulo: 'Custo',
                    render: (l) => fmtBRL(l.custoTotalBRL),
                  },
                  {
                    chave: 'lucro',
                    titulo: 'Lucro',
                    papel: 'destaque',
                    render: (l) => (
                      <span className={classeSinal(l.resultadoBRL)} style={{ fontWeight: 700 }}>
                        {l.resultadoBRL != null ? fmtBRL(l.resultadoBRL) : '—'}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          </>
        )}
      </Estado>
    </div>
  );
}
